import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Button,
  Link,
  Divider,
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import { Circle } from '@mui/icons-material';
import PSA_Icon from '../../assets/PSA_Icon+Type_CMYK.png';
import Image from 'mui-image';
import { isPsa2026NominationUrlEffective } from '../../utils/projectLinkEffectiveDates';

const PSA_NEW_NOMINATION_URL_LEGACY =
  'https://peointernational.communityforce.com/Funds/FundDetails.aspx?496F49596D593768464E3537506B6F754D5442315A306E30564A7645665648426D59714272334A50463566794B506D55552F3132555438504F4C61386C75316856577859322B563757586F3D';

const PSA_NEW_NOMINATION_URL_AUGUST_2026 =
  'https://peointernational.communityforce.com/Funds/FundDetails.aspx?496F49596D593768464E3537506B6F754D5442315A3542767349485273375750523775684767306D6C4C756473774E6D683474763570504D676E5737745662783532556D724B2F393876413D';

interface ButtonLinkProps {
  href: string;
  label: string;
}

const ButtonLink: React.FC<ButtonLinkProps> = ({ href, label }) => (
  <Button
    size="small"
    variant="text"
    color="primary"
    href={href}
    target="_blank"
    rel="noopener"
    sx={{
      textTransform: 'none',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: 'rgba(149, 149, 210, 0.08)',
      },
      color: '#861f41',
    }}
  >
    {label}
  </Button>
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`psa-tabpanel-${index}`}
    aria-labelledby={`psa-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

const PSALandingPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  const psaNewNominationUrl = isPsa2026NominationUrlEffective()
    ? PSA_NEW_NOMINATION_URL_AUGUST_2026
    : PSA_NEW_NOMINATION_URL_LEGACY;

  // Nomination dates roll over each Sept 1: before Sept show current year first, on/after Sept show next year first
  const nominationDateRows = React.useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const firstNominationYear =
      now.getMonth() >= 8 ? currentYear + 1 : currentYear;
    return [0, 1, 2, 3].map((i) => {
      const N = firstNominationYear + i;
      return {
        nominationFall: N,
        academicYear: `${N + 1}-${N + 2}`,
        graduationRange: `4/${N + 2} - 8/${N + 3}`,
      };
    });
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Hero Section with PSA Logo */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #9595D2 0%, #861f41 100%)',
          color: 'white',
          borderRadius: 3,
          p: 3,
          mb: 3,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Image
            src={PSA_Icon}
            alt="PSA Logo"
            sx={{ maxWidth: '100px' }}
            width={'40vw'}
          />
        </Box>
        <Typography
          variant="h4"
          sx={{ fontWeight: 'bold', mb: 1.5, color: '#fff' }}
        >
          PSA (P.E.O. Scholar Awards)
        </Typography>
        <Typography
          variant="body1"
          sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto', color: '#fff' }}
        >
          The Scholar Award is a one-time, competitive, merit-based award is
          intended to recognize and encourage academic excellence and
          achievement by women in doctoral-level programs. In addition to
          recognizing and encouraging excellence in higher education, these
          awards provide partial support for study and research for women who
          will make significant contributions in their varied fields of
          endeavor. All applicants are nominated by a local P.E.O. chapter.
        </Typography>
      </Paper>

      {/* Tab Navigation */}
      <Card sx={{ boxShadow: 3, borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="PSA page tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 500,
                minHeight: 64,
              },
            }}
          >
            <Tab label="Information & Resources" />
          </Tabs>
        </Box>

        {/* Information & Resources Tab */}
        <TabPanel value={tabValue} index={0}>
          {/* Local Chapter Recommendation Process */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
              mb: 4,
            }}
          >
            <CardHeader
              title="Local Chapter Recommendation Process"
              subheader="Once you have selected an applicant, follow the steps below to nominate her. The online nomination form is available every year from August 20 to November 20."
              sx={{
                background: 'linear-gradient(135deg, #9595D2 0%, #861f41 100%)',
                borderBottom: '1px solid #e0e0e0',
                color: '#fff',
              }}
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            />
            <CardContent sx={{ pb: 4 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      background:
                        'linear-gradient(135deg, #9595D2 0%, #861f41 100%)',
                      color: '#fff',
                      p: 1.5,
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    Step
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      background:
                        'linear-gradient(135deg, #9595D2 0%, #861f41 100%)',
                      color: '#fff',
                      p: 1.5,
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    Resources
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      <strong>1.</strong> Interview your candidate. Use the tips
                      document to prepare.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item container xs={12} md={6} spacing={1}>
                  <Grid item>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/3cc31862ea834be2b559bca97a7414c9/latest"
                      label="Interviewing/Nominating Tips for Potential Candidates"
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1.5 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      <strong>2.</strong> After a successful chapter vote,
                      complete the online PSA Nomination Form. You will need to
                      write a 200-300 word letter that shows why your candidate
                      stands out.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item container xs={12} md={6} spacing={1}>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/9f7c8f17f26848ef93105f3182a10cc7/latest"
                      label="Tips for Writing a Nomination Letter"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
            <CardActions sx={{ pb: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} textAlign="center">
                  <Link
                    href={psaNewNominationUrl}
                    target="_blank"
                    rel="noopener"
                    sx={{ textDecoration: 'none' }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        px: 4,
                        py: 1.5,
                        background:
                          'linear-gradient(135deg, #9595D2 0%, #861f41 100%)',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        boxShadow: 3,
                        '&:hover': {
                          boxShadow: 6,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Start New PSA Nomination
                    </Button>
                  </Link>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 600, mx: 'auto', mt: 1 }}
                  >
                    Click the button above to be directed to the CommunityForce
                    application system. CommunityForce is a third party vendor
                    we utilize for managing applications. Separate login
                    credentials will be required.
                    <br />
                    <Link
                      href="https://peo-portal.com/library/document/77fef27667fc4fb391e52322b710e0ff/latest"
                      target="_blank"
                      rel="noopener"
                    >
                      Instructions for starting a new online Nomination Form.
                    </Link>
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6} textAlign="center">
                  <Link
                    href="https://peointernational.communityforce.com"
                    target="_blank"
                    rel="noopener"
                    sx={{ textDecoration: 'none' }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        px: 4,
                        py: 1.5,
                        background:
                          'linear-gradient(135deg, #9595D2 0%, #861f41 100%)',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        boxShadow: 3,
                        '&:hover': {
                          boxShadow: 6,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Continue Existing PSA Nomination
                    </Button>
                  </Link>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 600, mx: 'auto', mt: 1 }}
                  >
                    Click the button above to be directed to the CommunityForce
                    application system. CommunityForce is a third party vendor
                    we utilize for managing applications. Separate login
                    credentials will be required.
                    <br />
                    <Link
                      href="https://peo-portal.com/library/document/f4b8177901e94f51ad99f4c618d74445/latest"
                      target="_blank"
                      rel="noopener"
                    >
                      Instructions for continuing an existing online Nomination
                      Form.
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </CardActions>
          </Card>

          {/* Eligibility Requirements */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
              mb: 4,
            }}
          >
            <CardHeader
              title="Eligibility Requirements"
              subheader="A woman is eligible for consideration for a P.E.O. Scholar Award if she:"
              sx={{
                background: 'linear-gradient(135deg, #9595D2 0%, #861f41 100%)',
                color: '#fff',
                borderBottom: '1px solid #e0e0e0',
              }}
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            />
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#861f41',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      is nominated by a local chapter of the P.E.O. Sisterhood
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#861f41',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      is a citizen or legal permanent resident of the U.S. or
                      Canada at the time of her nomination
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#861f41',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      at a minimum, has a bachelor's degree
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#861f41',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      is within two years of her graduation date and has at
                      least one full academic year of work remaining to complete
                      her doctoral-level degree as measured from the time the
                      first award payment is made (August 1). Those in a
                      multiple-degree program (e.g., pursuing both an M.D. and a
                      Ph.D.) must be within two years of completing the entire
                      program from that date.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#861f41',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      is enrolled in her program at an accredited U.S. or
                      Canadian college or university. Study abroad is permitted
                      provided the student is enrolled in a U.S. or Canadian
                      postsecondary institution.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    sx={{ p: 1.5, lineHeight: 1.6, fontStyle: 'italic' }}
                  >
                    The applications of individuals who satisfy these
                    eligibility criteria will be evaluated on a
                    nondiscriminatory basis without consideration of race, age,
                    national origin, religious affiliation, disability or P.E.O.
                    connection.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Application Process */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
              mb: 4,
            }}
          >
            <CardHeader
              title="Application Process"
              sx={{
                background: 'linear-gradient(135deg, #9595D2 0%, #861f41 100%)',
                color: '#fff',
                borderBottom: '1px solid #e0e0e0',
              }}
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            />
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    Applicants are nominated by local P.E.O. chapters. The
                    chapter nomination period is between August 20 and November
                    20 each year. After a chapter submits a nomination, the
                    P.E.O. Executive Office will email the applicant
                    instructions and a link to an online application.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    The nominee is responsible for completing the online
                    application packet and submitting it by the individually
                    designated due date. Each nominee is given 45 days to
                    complete the application. The nominee's due date will appear
                    on her application form.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    The form requires information about research or an essay
                    about the significance of the applicant's chosen field,
                    three letters of recommendation, evidence of scholarly
                    activities, awards, academic records, career objectives,
                    etc.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    All nominees will be notified of their award status by May
                    1. Prospective scholars must agree to certain conditions of
                    acceptance.
                  </Typography>
                  <Link
                    href="https://peo-portal.com/library/document/4d954ea1053341149e83a1649aea2baa/latest"
                    target="_blank"
                    rel="noopener"
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        p: 1.5,
                        lineHeight: 1.6,
                        color: '#861f41',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >
                      Find full details in the policies and procedures.
                    </Typography>
                  </Link>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    If you are interested in the P.E.O. Scholar Award but do not
                    know a P.E.O. member, you can fill out a potential applicant
                    form. Completing this form does not guarantee nomination for
                    an award.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Nomination Dates and Selection Criteria */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  boxShadow: 2,
                  borderRadius: 2,
                  overflow: 'hidden',
                  height: '100%',
                }}
              >
                <CardHeader
                  title="Nomination Dates for P.E.O. Scholar Awards"
                  sx={{
                    background:
                      'linear-gradient(135deg, #9595D2 0%, #861f41 100%)',
                    color: '#fff',
                    borderBottom: '1px solid #e0e0e0',
                  }}
                  titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
                />
                <CardContent sx={{ p: 2 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 'bold',
                          background:
                            'linear-gradient(135deg, #9595D2 0%, #861f41 100%)',
                          color: '#fff',
                          p: 1.5,
                          borderRadius: 1,
                          textAlign: 'center',
                        }}
                      >
                        Nomination by Local Chapter in Fall of:
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 'bold',
                          background:
                            'linear-gradient(135deg, #9595D2 0%, #861f41 100%)',
                          color: '#fff',
                          p: 1.5,
                          borderRadius: 1,
                          textAlign: 'center',
                        }}
                      >
                        Academic Year Applying for:
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 'bold',
                          background:
                            'linear-gradient(135deg, #9595D2 0%, #861f41 100%)',
                          color: '#fff',
                          p: 1.5,
                          borderRadius: 1,
                          textAlign: 'center',
                        }}
                      >
                        Nominee Graduation Date Range:
                      </Typography>
                    </Grid>
                    {nominationDateRows.map((row) => (
                      <React.Fragment key={row.nominationFall}>
                        <Grid item xs={4}>
                          <Typography
                            variant="body1"
                            sx={{ p: 1.5, textAlign: 'center' }}
                          >
                            {row.nominationFall}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography
                            variant="body1"
                            sx={{ p: 1.5, textAlign: 'center' }}
                          >
                            {row.academicYear}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography
                            variant="body1"
                            sx={{ p: 1.5, textAlign: 'center' }}
                          >
                            {row.graduationRange}
                          </Typography>
                        </Grid>
                      </React.Fragment>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  boxShadow: 2,
                  borderRadius: 2,
                  overflow: 'hidden',
                  height: '100%',
                }}
              >
                <CardHeader
                  title="Selection Criteria"
                  subheader="The Scholar Awards Board of Trustees evaluate applicants on the following:"
                  sx={{
                    background:
                      'linear-gradient(135deg, #9595D2 0%, #861f41 100%)',
                    color: '#fff',
                    borderBottom: '1px solid #e0e0e0',
                  }}
                  titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
                />
                <CardContent sx={{ p: 2 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                        }}
                      >
                        <Circle
                          sx={{
                            fontSize: 12,
                            color: '#861f41',
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          nominee's proposal
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                        }}
                      >
                        <Circle
                          sx={{
                            fontSize: 12,
                            color: '#861f41',
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          potential to make significant contributions in her
                          field(s) of endeavor or to make a positive impact on
                          society
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                        }}
                      >
                        <Circle
                          sx={{
                            fontSize: 12,
                            color: '#861f41',
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          recommendations from three university professors or
                          professional mentors
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                        }}
                      >
                        <Circle
                          sx={{
                            fontSize: 12,
                            color: '#861f41',
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          evidence of scholarly activities (publications,
                          presentations, patents, performances, etc.)
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                        }}
                      >
                        <Circle
                          sx={{
                            fontSize: 12,
                            color: '#861f41',
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          academic awards and honors (honor societies,
                          scholarships, etc.)
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                        }}
                      >
                        <Circle
                          sx={{
                            fontSize: 12,
                            color: '#861f41',
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          academic record
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                        }}
                      >
                        <Circle
                          sx={{
                            fontSize: 12,
                            color: '#861f41',
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          career objectives
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                        }}
                      >
                        <Circle
                          sx={{
                            fontSize: 12,
                            color: '#861f41',
                            mt: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          Examples of individual leadership and cultural
                          experiences
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        sx={{
                          p: 1.5,
                          lineHeight: 1.6,
                          fontStyle: 'italic',
                          color: '#861f41',
                        }}
                      >
                        <b>Note: </b>It is often better to apply further into
                        the doctoral program when evidence of achievement is
                        more complete, giving a more thorough picture of an
                        applicant's professional work.
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>

      {/* Details */}
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          overflow: 'hidden',
          mb: 4,
        }}
      >
        <CardHeader
          title="Details About the Scholarship"
          sx={{
            background: 'linear-gradient(135deg, #9595D2 0%, #861f41 100%)',
            color: '#fff',
            borderBottom: '1px solid #e0e0e0',
          }}
          titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
        />
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                PSA provides a one-time scholarship of $25,000 over one year.
                Scholarships may be awarded to educational institutions in the
                United States or Canada.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                The Scholarship can be used for necessary expenses while
                pursuing a doctoral-level degree. These include:
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                }}
              >
                <Circle
                  sx={{
                    fontSize: 12,
                    color: '#861f41',
                    mt: 0.5,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  Normal living expenses during doctoral study,
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                }}
              >
                <Circle
                  sx={{
                    fontSize: 12,
                    color: '#861f41',
                    mt: 0.5,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  Funds that directly enhance, enable, or support the
                  research-based or professional programs,
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                }}
              >
                <Circle
                  sx={{
                    fontSize: 12,
                    color: '#861f41',
                    mt: 0.5,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  Travel expenses for research and conferences,
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                }}
              >
                <Circle
                  sx={{
                    fontSize: 12,
                    color: '#861f41',
                    mt: 0.5,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  Modest tokens of appreciation to research subjects,
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                }}
              >
                <Circle
                  sx={{
                    fontSize: 12,
                    color: '#861f41',
                    mt: 0.5,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  Assistance for undergraduate researchers needed to complete
                  research,
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                }}
              >
                <Circle
                  sx={{
                    fontSize: 12,
                    color: '#861f41',
                    mt: 0.5,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  Outreach required for project completion,
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                }}
              >
                <Circle
                  sx={{
                    fontSize: 12,
                    color: '#861f41',
                    mt: 0.5,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  Expenses used before graduation for post-doc or internship
                  interviews.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Become a Laureate Chapter  */}
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          overflow: 'hidden',
          mb: 4,
        }}
      >
        <CardHeader
          title="Become a Laureate Chapter"
          sx={{
            background: 'linear-gradient(135deg, #9595D2 0%, #861f41 100%)',
            color: '#fff',
            borderBottom: '1px solid #e0e0e0',
          }}
          titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
        />
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                The P.E.O. Scholar Awards Laureate Chapter Program is designed
                to encourage local chapters to promote and support the Scholar
                Awards project. By making a lump-sum contribution of $500 or
                more, a chapter can proudly bear the title of a “P.E.O. Scholar
                Awards Laureate Chapter." This recognition not only highlights a
                chapter’s dedication, but also gives participating P.E.O.
                chapters a way to showcase their commitment to empowering women
                in their higher education endeavors.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                To become a Laureate Chapter, make sure your chapter's
                contribution reaches your state, provincial or district (s/p/d)
                treasurer or executive assistant by January 31. (Chapters in
                Hawaii should send their contributions to the International
                Chapter Treasurer).
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                Individuals can also play a pivotal role in this legacy journey.
                Lump-sum donations of $500 or more qualify your chapter as a
                Laureate Chapter. Specify your chapter's name on the Individual
                Donation Form to create this legacy for your chapter. To be
                considered a charitable donation, this contribution must be made
                payable to P.E.O. Scholar Awards and be sent to the P.E.O.
                Executive Office by January 31.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                S/P/D chapter gift transmittals that include donations
                qualifying for the Laureate Chapter program must be received at
                the P.E.O. Executive Office in Des Moines no later than February
                28/29 to be recognized at the upcoming s/p/d convention. Gifts
                received by the executive office after February 28/29 will be
                processed and recognized at the following year’s convention.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PSALandingPage;
